// Shim jquery required functions for signalR
// Some are picked from jquery 2.1.1
/// <amd-module name="VSS/Features/SignalR/3rdParty/jquery.shimmed" />
export interface IDeferred {
    promise: () => Promise<any>;
    then: (onfulfilled?: ((value: any) => {} | PromiseLike<{}>) | null | undefined, onrejected?: ((reason: any) => {} | PromiseLike<{}>) | null | undefined) => Promise<any>;
    resolve: (value?: any) => void;
    reject: (value: any) => any;
}

export interface IJqueryFunction {
    bind: (event: any, handler: any) => void;
    unbind: (event: any, handler?: any) => void;
    [index: number]: any;
}

export interface IJqueryWindowFunction extends IJqueryFunction {
    load: (handler: any) => void;
}

export interface IJqueryObjectFunction extends IJqueryFunction {
    triggerHandler: (event: any, args?: any) => void;
}

export interface IJqueryShimSupported {
    (obj: any): IJqueryWindowFunction | IJqueryObjectFunction;
    isArray(obj: any): boolean;
    isFunction(obj: any): boolean;
    support: {
        cors: boolean;
    };
    each: (obj: any[], callback: any) => void;
    inArray: (element: any, array: any[]) => number;
    ajax: (options: any) => { abort: (reason: string) => any };
    isEmptyObject: (obj: any) => boolean;
    makeArray: (obj: any) => any[];
    merge: (first: any, second: any) => any;
    Deferred: () => IDeferred;

    // custom injected objects
    connection?: any;
    signalR?: any;
    hubConnection?: any;
}

function isArray(obj: any) {
    return Array.isArray(obj) || obj instanceof Array;
}

function isFunction(obj: any) {
    return typeof obj == "function" || obj instanceof Function;
}

function isWindow(obj: any) {
    return obj != null && obj === obj.window;
}

var jqueryFunction = (obj: any): IJqueryWindowFunction | IJqueryObjectFunction => {
    if (obj === obj.window) {
        const object: Window = obj;

        return {
            0: object,
            load: handler => object.addEventListener("load", handler, false),
            bind: (event, handler) => object.addEventListener(event, handler, false),
            unbind: (event, handler?) => object.removeEventListener(event, handler, false)
        };
    }

    let handlers: any[] = obj.jHandlers || [];
    return {
        0: obj,
        bind: (event: any, handler: any) => {
            const currentHandlers = handlers[event] || [];
            handlers[event] = currentHandlers.concat(handler);
            obj.jHandlers = handlers;
        },
        unbind: (event: any, handler?: any) => {
            let currentHandlers = handlers[event] || [];
            if (handler) {
                let index = currentHandlers.indexOf(handler);
                if (index !== -1) {
                    currentHandlers.splice(index, 1);
                }
            } else {
                currentHandlers = [];
            }

            handlers[event] = currentHandlers.concat(handler);
            obj.jHandlers = handlers;
        },
        triggerHandler: (event: any, args?: any) => {
            const eventHandlers = handlers[event] || [];
            eventHandlers.forEach((handler: any) => {
                if (args && args[0] && args[0].type === undefined) {
                    args = [
                        {
                            type: event
                        }
                    ].concat(args || []);
                } else {
                    args = args || [];
                }

                handler.apply(this, args);
            });
        }
    };
};

function merge(first: any, second: any) {
    var len = +second.length,
        j = 0,
        i = first.length;

    for (; j < len; j++) {
        first[i++] = second[j];
    }

    first.length = i;

    return first;
}

function isArraylike(obj: any) {
    var length = obj.length,
        type: string = typeof obj;

    if (type === "function" || isWindow(obj)) {
        return false;
    }

    if (obj.nodeType === 1 && length) {
        return true;
    }

    return type === "array" || length === 0 || (typeof length === "number" && length > 0 && length - 1 in obj);
}

const xhr = function() {
    try {
        return new (window as any).XMLHttpRequest();
    } catch (e) {
        console.error(e);
    }
};

const ajax = function(options: any) {
    const request = xhr();

    request.onreadystatechange = () => {
        if (request.readyState !== 4) {
            return;
        }

        if (request.status === 200 && !request._hasError) {
            try {
                options.success && options.success(JSON.parse(request.responseText));
            } catch (e) {
                options.error && options.error(request);
            }
        } else {
            options.error && options.error(request);
        }
    };

    request.withCredentials = options.xhrFields.withCredentials;
    request.open(options.type, options.url);
    request.setRequestHeader("content-type", options.contentType);

    if (options.beforeSend) {
        options.beforeSend(request)
            .then(() => {
                request.send(options.data.data && `data=${options.data.data}`);
            });
    }
    else {
        request.send(options.data.data && `data=${options.data.data}`);
    }

    return {
        abort: function(reason: string) {
            return request.abort(reason);
        }
    };
};

function Deferred(): IDeferred {
    let _resolve: (value?: any) => void = () => {};
    let _reject: (value: any) => void = () => {};
    let _promise: Promise<any> = new Promise((resolve, reject) => {
        _resolve = resolve;
        _reject = reject;
    });

    return {
        promise: () => {
            return _promise;
        },
        then: _promise.then.bind(_promise),
        resolve: _resolve.bind(_promise),
        reject: _reject.bind(_promise)
    };
}

export var jqueryShimmed = jqueryFunction as IJqueryShimSupported;

jqueryShimmed.isArray = isArray;
jqueryShimmed.support = {
    cors: (() => {
        const xhrObj = xhr();
        return !!xhrObj && "withCredentials" in xhrObj;
    })()
};
jqueryShimmed.each = (obj: any[], callback: any) => {
    const object = obj || [];
    if (isArray(object)) {
        object.forEach((value, index) => {
            callback(index, value);
        });
    } else {
        Object.keys(object).forEach((key: any) => {
            const currentObject = object[key];
            callback.call(currentObject, key, currentObject);
        });
    }
};
jqueryShimmed.inArray = (element: any, array: any[]) => {
    const indexOf = Array.prototype.indexOf;
    if (!array) {
        return -1;
    }

    if (indexOf) {
        return indexOf.call(array, element);
    }

    for (var i = 0, length = array.length; i < length; i++) {
        if (array[i] === element) {
            return i;
        }
    }

    return -1;
};
jqueryShimmed.ajax = ajax;
jqueryShimmed.isEmptyObject = (obj: any) => {
    for (let name in obj) {
        return false;
    }

    return true;
};
jqueryShimmed.isFunction = isFunction;
jqueryShimmed.makeArray = (obj: any) => {
    var ret: any[] = [];

    if (obj != null) {
        if (isArraylike(Object(obj))) {
            merge(ret, typeof obj === "string" ? [obj] : obj);
        } else {
            ret.push(ret, obj);
        }
    }

    return ret;
};
jqueryShimmed.Deferred = Deferred;
jqueryShimmed.merge = merge;
